
export enum UserStatus {
  initial = 'initial',
  regular = 'regular',
}

export enum UserRole {
  patient = 'patient',
  provider = 'provider',
  superadmin = 'superadmin',
  externalAdmin = 'external_admin'
}

export enum UserRestrictions {
  nonpayment = 'nonpayment'
}

export enum AdminRole {
  superAdmin = 'super_admin',
  admin = 'admin',
  cxAdmin = 'cx_admin',
  cxSpecialist = 'cx_specialist',
  billingSpecialist = 'billing_specialist',
  credentiallingSpecialist = 'credentialling_specialist',
  clinicalSpecialist = 'clinical_specialist',
  higherEdAdmin = 'higher_ed_admin',
  developer = 'developer',
}

export const enum SubSystem {
  higherEd = 'higher_ed',
  betterhelp = 'betterhelp',
}

// Mirrors the backend's permission catalog (uplift/common/permissions/keys.py) key-for-key --
// one namespace per catalog page, one member per page/field action. Every permission check must
// reference a member here instead of a hardcoded string, so a typo'd key fails to compile rather
// than silently never matching.
export const AdminPermissions = {
  InsuranceMarket: {
    View: 'insurance_markets.view',
    Write: 'insurance_markets.write',
    Delete: 'insurance_markets.delete',
    NameView: 'insurance_markets.name.view',
    NameWrite: 'insurance_markets.name.write',
    PayorIdView: 'insurance_markets.payor_id.view',
    PayorIdWrite: 'insurance_markets.payor_id.write',
    KeyView: 'insurance_markets.key.view',
    KeyWrite: 'insurance_markets.key.write',
    UrlView: 'insurance_markets.url.view',
    UrlWrite: 'insurance_markets.url.write',
    LogoUrlView: 'insurance_markets.logo_url.view',
    LogoUrlWrite: 'insurance_markets.logo_url.write',
    OrderView: 'insurance_markets.order.view',
    OrderWrite: 'insurance_markets.order.write',
    MetaGroupingView: 'insurance_markets.meta_grouping.view',
    MetaGroupingWrite: 'insurance_markets.meta_grouping.write',
  },
  Admins: {
    View: 'admins.view',
    Write: 'admins.write',
  },
  FeatureFlags: {
    View: 'feature_flags.view',
    Write: 'feature_flags.write',
  },
  RolesAndPermissions: {
    View: 'roles_and_permissions.view',
    Write: 'roles_and_permissions.write',
  },
  VendorSync: {
    View: 'vendor_sync.view',
    Manage: 'vendor_sync.manage',
  },
  States: {
    View: 'states.view',
    Write: 'states.write',
  },
  UserDirectory: {
    Impersonate: 'user_directory.impersonate',
    View: 'user_directory.view',
  },
  Appointments: {
    Write: 'appointments.write',
    ManagePayment: 'appointments.manage_payment',
  },
  ProviderCredentialing: {
    View: 'provider_credentialing.view',
    Write: 'provider_credentialing.write',
    ViewOnboarding: 'provider_credentialing.view_onboarding',
    Manage: 'provider_credentialing.manage',
  },
  Payouts: {
    View: 'payouts.view',
    Write: 'payouts.write',
  },
  CandidPayors: {
    View: 'candid_payors.view',
    Write: 'candid_payors.write',
    Manage: 'candid_payors.manage',
  },
  PublicProviderUrls: {
    Manage: 'public_provider_urls.manage',
  },
  ClientDeidExport: {
    View: 'client_deid_export.view',
  },
  BetterhelpCoverage: {
    Write: 'betterhelp_coverage.write',
  },
  Matching: {
    Write: 'matching.write',
    Delete: 'matching.delete',
  },
  FormBuilder: {
    Write: 'form_builder.write',
  },
  PatientReferral: {
    View: 'patient_referral.view',
  },
  PatientBilling: {
    View: 'patient_billing.view',
    Write: 'patient_billing.write',
  },
  TaskExecution: {
    Write: 'task_execution.write',
  },
  UserLifecycle: {
    Manage: 'user_lifecycle.manage',
  },
  Messaging: {
    View: 'messaging.view',
  },
  MessagingReport: {
    View: 'messaging_report.view',
  },
  AgreementTemplates: {
    Manage: 'agreement_templates.manage',
  },
  CollaborativePhysicians: {
    View: 'collaborative_physicians.view',
  },
  BusinessEntities: {
    Manage: 'business_entities.manage',
  },
  VerifiableLicenseTypeMapping: {
    Manage: 'verifiable_license_type_mapping.manage',
  },
  Tags: {
    Manage: 'tags.manage',
  },
  ProviderActivationEvents: {
    Manage: 'provider_activation_events.manage',
  },
  ClientEligibilityReport: {
    View: 'client_eligibility_report.view',
  },
  ClientOutstandingBalanceReport: {
    View: 'client_outstanding_balance_report.view',
  },
  PastSessionsReport: {
    View: 'past_sessions_report.view',
  },
  CancelledSessionsReport: {
    View: 'cancelled_sessions_report.view',
  },
  UnsubmittedSessionsReport: {
    View: 'unsubmitted_sessions_report.view',
  },
  LicenseStatusReport: {
    View: 'license_status_report.view',
  },
  CredentialingStatusReport: {
    View: 'credentialing_status_report.view',
  },
  ProviderMatchingReport: {
    View: 'provider_matching_report.view',
  },
  PsychiatryOnlyUTMSources: {
    Manage: 'psychiatry_only_utm_sources.manage',
  },
} as const;

type DeepPermissionValue<T> = T extends string ? T : T extends Record<string, unknown> ? DeepPermissionValue<T[keyof T]> : never;

export type AdminPermissionKey = DeepPermissionValue<typeof AdminPermissions>;
